// src/components/AdminDashboard.tsx
import React, { useState, useEffect } from "react";
import { Box, Select, MenuItem, Button, Typography, FormControl, InputLabel, LinearProgress, TextField } from "@mui/material";
import Papa from "papaparse";
import { collection, getDocs, deleteDoc, doc, writeBatch, addDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { raceTabs, calculateRankings } from "../utils/calculatePoints";

const AdminDashboard: React.FC = () => {
  const { logout } = useAuth();
  const [selectedRace, setSelectedRace] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [rankings, setRankings] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    calculateRankings().then(setRankings);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFile(e.target.files[0]);
  };

  const handleTitleSave = async () => {
    if (!title) {
      setUploadStatus("Please enter a title.");
      return;
    }
    try {
      await setDoc(doc(db, "settings", "siteConfig"), { title });
      setUploadStatus("Title saved successfully!");
    } catch (error: any) {
      setUploadStatus(`Error saving title: ${error.message || "Unknown error"}`);
      console.error("Firestore title save error:", error);
    }
  };

  const handleUpload = async () => {
    console.log("Upload started...");
    console.log("Selected Race:", selectedRace);
    console.log("Selected File:", file?.name);

    if (!selectedRace || !file) {
      setUploadStatus("Select a race and file.");
      console.warn("Missing race or file");
      return;
    }

    setIsUploading(true);
    setUploadStatus("Clearing existing data...");
    setUploadProgress({ current: 0, total: 0 });

    try {
      // Delete existing documents
      const raceCollection = collection(db, selectedRace);
      const snapshot = await getDocs(raceCollection);
      console.log(`Found ${snapshot.size} existing documents in ${selectedRace}`);

      const batch = writeBatch(db);
      snapshot.forEach((docSnapshot) => {
        batch.delete(doc(raceCollection, docSnapshot.id));
      });
      await batch.commit();
      console.log(`Deleted ${snapshot.size} documents from ${selectedRace}`);

      // Parse and upload new CSV data
      setUploadStatus("Parsing CSV...");
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results: any) => {
          console.log("PapaParse complete:", results.data);
          const validRows = results.data.filter(
            (row: any) => row.RACE_NAME && row.FIRST_NAME && row.LAST_NAME && row.PRIMARY_BRACKET && row.PRIMARY_BRACKET_RANK
          );
          const total = validRows.length;
          setUploadProgress({ current: 0, total });
          setUploadStatus(`Uploading 0 / ${total} records...`);

          try {
            let current = 0;
            for (const row of validRows) {
              console.log("Adding doc to Firestore:", row);
              await addDoc(raceCollection, row);
              current++;
              setUploadProgress({ current, total });
              setUploadStatus(`Uploading ${current} / ${total} records...`);
            }
            setUploadStatus("Upload successful! Rankings will update automatically.");
            console.log("Upload completed successfully");
            // Refresh rankings after upload
            calculateRankings().then(setRankings);
          } catch (error: any) {
            setUploadStatus(`Error: ${error.message || "Unknown error"}`);
            console.error("Firestore upload error:", error);
          } finally {
            setIsUploading(false);
          }
        },
        error: (error: any) => {
          setUploadStatus(`Parse error: ${error.message}`);
          console.error("PapaParse error:", error);
          setIsUploading(false);
        },
      });
    } catch (error: any) {
      setUploadStatus(`Error clearing data: ${error.message || "Unknown error"}`);
      console.error("Firestore deletion error:", error);
      setIsUploading(false);
    }
  };

  const calculateAge = (dob: string | null): number | "N/A" => {
    if (!dob) return "N/A";
    const birthDate = new Date(dob);
    const ageDifMs = Date.now() - birthDate.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const handleDownload = () => {
    // Flatten rankings data into CSV rows
    const csvData: any[] = [];
    Object.entries(rankings).forEach(([distance, brackets]: any) => {
      Object.entries(brackets).forEach(([bracket, participants]: any) => {
        participants.forEach((p: any) => {
          const eventsData: { [key: string]: number } = {};
          raceTabs.forEach(race => {
            eventsData[race] = p.events[race] || 0;
          });
          csvData.push({
            Distance: distance,
            Bracket: bracket,
            Name: p.name,
            Rank: p.rank,
            TotalPoints: p.totalPoints,
            Events: p.eventCount,
            City: p.city,
            State: p.regionCode,
            Email: p.email,
            Gender: p.gender,
            Age: calculateAge(p.dob),
            DOB: p.dob || "N/A",
            ...eventsData, // Spread per-event points for all races
          });
        });
      });
    });

    if (csvData.length === 0) {
      setUploadStatus("No data available to download.");
      return;
    }

    // Generate CSV
    const csv = Papa.unparse(csvData, { header: true });

    // Create and download blob
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "participants-results.csv");
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Admin Dashboard</Typography>
      
      {/* Title Input */}
      <Typography variant="h6" sx={{ mb: 1 }}>Set Site Title</Typography>
      <TextField
        label="Site Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        placeholder="e.g., 2025 San Diego Tri Series"
      />
      <Button variant="contained" onClick={handleTitleSave} sx={{ mb: 2 }}>Save Title</Button>

      <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>Upload Race Data</Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Race</InputLabel>
        <Select value={selectedRace} onChange={(e) => setSelectedRace(e.target.value)}>
          {raceTabs.map((race) => (
            <MenuItem key={race} value={race}>{race.replace(/([A-Z])/g, " $1").trim()}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <input type="file" accept=".csv" onChange={handleFileChange} style={{ marginBottom: "16px" }} />
      <Button variant="contained" onClick={handleUpload} sx={{ mr: 2 }} disabled={isUploading}>Upload CSV</Button>
      <Button variant="outlined" onClick={logout}>Logout</Button>
      {uploadStatus && <Typography sx={{ mt: 2, color: uploadStatus.includes("Error") ? "red" : "green" }}>{uploadStatus}</Typography>}
      {isUploading && (
        <LinearProgress
          variant="determinate"
          value={(uploadProgress.current / uploadProgress.total) * 100 || 0}
          sx={{ mt: 2 }}
        />
      )}

      {/* Download Results Button */}
      <Typography variant="h6" sx={{ mb: 1, mt: 4 }}>Download Data</Typography>
      <Button variant="contained" onClick={handleDownload}>Download Results</Button>
    </Box>
  );
};

export default AdminDashboard;